import { ContractInfo } from 'app/core/store/contract-info/contract-info.interface';
import { CustomerInfo } from 'app/core/store/customer-info/customer-info.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { util } from '../../util/util';
import { DeliveryMethod, FlowDetailTO, PackingMaterial } from '../edit-florder-detail/florder-detail.interface';
import { initialStatePackingMaterial } from '../edit-florder-detail/florder-detail.model';
import * as actions from './flow-detail.actions';
import { EditFlowDetail } from './flow-detail.interface';
import { initialStateEditFlowDetail } from './flow-detail.model';

export function reducer(state = util.deepCopy(initialStateEditFlowDetail), action: actions.Actions): EditFlowDetail {
  /**
   * Add or replace a material in an existing array of materials
   * @param material
   * @param returnState
   * @param definitions
   */
  function addOrReplaceMaterial(material: PackingMaterial, returnState: EditFlowDetail, definitions: Definitions) {
    const existingMaterial = !!state.flowDetail.materials.find(m => m.internalId === material.internalId);
    if (existingMaterial) {
      return Object.assign({}, returnState, {
        flowDetail: Object.assign({}, returnState.flowDetail, {
          materials: sortMaterials(
            state.flowDetail.materials.map(m => (m.internalId === material.internalId ? material : m)),
            state.sort,
            definitions
          ),
        }),
      });
    } else {
      return Object.assign({}, returnState, {
        flowDetail: Object.assign({}, returnState.flowDetail, {
          materials: sortMaterials([...state.flowDetail.materials, material], state.sort, definitions),
        }),
      });
    }
  }

  /**
   * Create a new material
   * @param transientInternalId
   * TODO use this function in other reducers too
   */
  function createNewMaterial(transientInternalId: number = 0) {
    const newMaterial = util.deepCopy(initialStatePackingMaterial) as PackingMaterial;
    newMaterial.internalId = Math.max(0, transientInternalId, ...state.flowDetail.materials.map(m => m.internalId)) + 1; // Necessary so that when edited can be found back . Will be 0 when posted thanks to isNew = true
    newMaterial.isNew = true; // Will set internalId = 0 when sending POST request.  Keep it 'true' !!!
    return newMaterial;
  }

  /**
   * Read data from SAP, transform in data for state
   * @param dataTO
   * @param customerInfo
   * @param contractInfo
   * @param isHandshakerOrLateHandshake
   * @param type
   * @param definitions
   * @param customer
   * @param lineItemsAreEditable set isEditable=true (like for copy, edit or update handshake)
   */
  function readFlowTO(
    dataTO: FlowDetailTO,
    customerInfo: CustomerInfo,
    contractInfo: ContractInfo,
    isHandshakerOrLateHandshake: boolean,
    type: 'FLOWH' | 'FLOWR',
    definitions: Definitions,
    customer: Customer,
    lineItemsAreEditable = false
  ) {
    const wholesaler = dataTO.wholeSalerId ? definitions.flow.wholesaler.find(w => w.id === dataTO.wholeSalerId) : null;
    const editFlowDetail = {
      ...state,
      isDisputeMgmt: false,
      mode: {
        ...state.mode,
        loading: false,
        loadSuccess: true,
      },
      flowDetail: {
        deliveryMethod: {
          to: dataTO.to,
          from: dataTO.from,
          transport: dataTO.transport,
          customer,
          clearing: dataTO.clearing === 'yes' ? true : dataTO.clearing === 'no' ? false : null,
          customerReferenceNumber: dataTO.customerRefNumber,
          originalReferenceNumber: dataTO.originalRefNumber,
          senderReferenceNumber: dataTO.senderRefNumber,
          transporter: dataTO.transporter,
          licensePlate: dataTO.licensePlate,
          remarks: dataTO.comments,
          remarksHandshaker: dataTO.handshakeComments,
          wholesaler,
        },
        planning: {
          flowDate: dataTO.flowDate,
          transferDate: dataTO.transferDate,
          loadingDate: dataTO.confirmedLoadingDate || dataTO.confirmedUnloadingDate || dataTO.requestedUnloadingDate || dataTO.requestedLoadingDate, // TODO
          requestedUnloadingDate: dataTO.requestedUnloadingDate,
          confirmedUnloadingDate: dataTO.confirmedUnloadingDate,
        },
        etmOrderNumber: dataTO.etmOrderNumber,
        salesOrderNumber: dataTO.salesOrderNumber,
        shipmentNumbers: dataTO.shipmentNumber,
        registeredBy: dataTO.registeredBy,
        globalType: dataTO.globalType,
        isLocked: !!dataTO.isLocked,
      },
    } as EditFlowDetail;

    // Fix for jira issue 2688
    const handshaker = dataTO.executeHandshake;

    const lateHandshake = !handshaker && isHandshakerOrLateHandshake; // TODO should come from SAP

    let materials: PackingMaterial[] = dataTO.flowLineItems.map(li => {
      return {
        materialType: 'flowMaterial',
        type: 'packing',
        internalId: li.itemNumber,
        packingId: li.packingId,
        packingStatus: li.packingStatus,
        logisticsVarietyPacking: li.logisticsVarietyPacking,
        packingQuantity: li.definitiveQuantity || li.originalQuantity,
        poNumberItemLevel: '',
        contentId: li.contentId,
        lineReferenceId: li.lineReferenceId,
        originalQuantity: li.originalQuantity,
        definitiveQuantity: li.definitiveQuantity,
        difference: li.differenceQuantity,
        isEditable: lineItemsAreEditable,
        differenct: li.differenceQuantity,
        status: li.handshakeStatus,
        poItemNumber: '',
        isNew: false,
      } as PackingMaterial;
    });
    materials = sortMaterials(materials, state.sort, definitions);

    return {
      ...editFlowDetail,
      flowDetail: {
        ...editFlowDetail.flowDetail,
        customer,
        status: dataTO.handshakeStatus,
        shipmentNumbers: dataTO.shipmentNumber,
        deliveryMethod: {
          ...editFlowDetail.flowDetail.deliveryMethod,
          type: dataTO.flowType,
        },
        planning: {
          ...editFlowDetail.flowDetail.planning,
          flowDate: dataTO.flowDate,
          definitiveDate: dataTO.definitiveDate,
        },
        materials,
        registrar: !handshaker,
        handshaker,
        lateHandshake,
        updateDate: dataTO.updateDate,
        updateTime: dataTO.updateTime,
        updateBy: dataTO.updateBy,
      },
    };
  }

  /**
   * Sort materials.  does not affect the state. is isolated function
   * @param materials
   * @param sort
   * @param definitions
   */
  function sortMaterials(materials: PackingMaterial[], sort: { sortField: string; sortAscending: boolean }, definitions: Definitions): PackingMaterial[] {
    const list = [...materials];
    list.sort((m1, m2) => {
      let f1 = m1[sort.sortField];
      let f2 = m2[sort.sortField];
      if (sort.sortField === 'lineReferenceId') {
        f1 = util.toIntegerIfPossible(f1);
        f2 = util.toIntegerIfPossible(f2);
      }
      if (sort.sortField === 'packingId' && definitions) {
        f1 = definitions.packing.type.find(packing => packing.id === f1).name;
        f2 = definitions.packing.type.find(packing => packing.id === f2).name;
      }
      const result = f1 > f2 ? 1 : f1 < f2 ? -1 : 0;
      return result * (sort.sortAscending ? 1 : -1);
    });
    return list;
  }

  /**
   * Does extra reducing & mapping in case of an edit dispute mgmt flow
   * @param editFlowDetail
   */
  function disputeReducer(editFlowDetail: EditFlowDetail, changedMaterial?: PackingMaterial): EditFlowDetail {
    // If not dispute managment, skip this function
    if (!editFlowDetail.isDisputeMgmt) {
      return editFlowDetail;
    }

    // calculate saldo count for materials, determine if saldo column must be visible
    let statistics: { [id: string]: { definitiveSum: number; disputeSum: number; saldo: number; count: number } } = {};
    let showSaldo = false;
    statistics = editFlowDetail.flowDetail.materials
      .filter(m => m.isEditable)
      .reduce((result, material) => {
        const saldo = result[material.packingId];

        return {
          ...result,
          [material.packingId]: {
            definitiveSum: (saldo ? saldo.definitiveSum : 0) + parseInt('' + material.definitiveQuantity, 10),
            disputeSum: (saldo ? saldo.disputeSum : 0) + parseInt('' + material.disputeQuantity, 10),
            count: (saldo ? saldo.count : 0) + 1,
            saldo: null,
          },
        };
      }, {});

    // calculate saldo
    Object.keys(statistics).forEach(packingId => {
      if (statistics[packingId].count > 2) {
        statistics[packingId].saldo = statistics[packingId].definitiveSum - statistics[packingId].disputeSum;
        showSaldo = showSaldo || statistics[packingId].saldo !== 0;
      }
    });
    let materials = editFlowDetail.flowDetail.materials.map(material => {
      if (statistics[material.packingId] && statistics[material.packingId].saldo !== null) {
        return {
          ...material,
          saldo: statistics[material.packingId].saldo === 0 ? null : statistics[material.packingId].saldo,
        };
      }
      return { ...material, saldo: null };
    });

    // check if automatic distribution can be done
    //   -> distribute if exactly 2 materials of each type
    if (changedMaterial && statistics[changedMaterial.packingId] && statistics[changedMaterial.packingId].count === 2) {
      const theOtherMaterial = materials.find(m => m.packingId === changedMaterial.packingId && m.internalId !== changedMaterial.internalId);
      const stats = statistics[changedMaterial.packingId];
      materials = materials.map(material => {
        const disputeQuantity = Math.max(0, stats.definitiveSum - parseInt('' + changedMaterial.disputeQuantity, 10));
        if (material === theOtherMaterial) {
          return {
            ...material,
            disputeQuantity,
            packingQuantity: disputeQuantity,
          };
        }
        return material;
      });
    }

    return {
      ...editFlowDetail,
      flowDetail: {
        ...editFlowDetail.flowDetail,
        materials,
      },
      showSaldo,
    };
  }

  // THE REDUCERS
  switch (action.type) {
    case actions.FLOWDETAIL_CREATE_START: {
      const pristineState = util.deepCopy(initialStateEditFlowDetail);
      return Object.assign({}, pristineState, {
        mode: {
          type: action.module,
          state: 'create',
          editWindow: action.payload.isSuperUser ? 'customer' : 'delivery',
        },
        flowDetail: {
          ...pristineState.flowDetail,
          customer: action.payload.isSuperUser ? null : action.payload.selectedCustomer,
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_SET_CUSTOMER: {
      const pristineState = util.deepCopy(initialStateEditFlowDetail);
      const customerChanged = (!state.flowDetail.customer && action.payload.customer) || (state.flowDetail.customer && state.flowDetail.customer.id !== action.payload.customer.id);
      return Object.assign({}, pristineState, {
        ...pristineState,
        mode: {
          type: action.module,
          state: state.mode.state,
          editWindow: state.mode.state === 'create' ? 'delivery' : null,
          createTemplate: state.mode.createTemplate,
          editTemplate: state.mode.editTemplate,
        },
        flowDetail: customerChanged
          ? {
            ...pristineState.flowDetail,
            customer: action.payload.customer,
          }
          : state.flowDetail,
      });
    }

    case actions.FLOWDETAIL_CREATE_SET_DELIVERY_METHOD: {
      const material = util.deepCopy(initialStatePackingMaterial) as PackingMaterial;
      material.internalId = Math.max(0, ...state.flowDetail.materials.map(m => m.internalId)) + 1;
      material.isNew = true;

      const fromToChanged =
        (state.flowDetail.deliveryMethod.to && state.flowDetail.deliveryMethod.to.id) !== action.payload.to.id ||
        (state.flowDetail.deliveryMethod.from && state.flowDetail.deliveryMethod.from.id) !== action.payload.from.id;

      return Object.assign({}, state, {
        mode: {
          type: action.module,
          state: state.mode.state === 'edit' ? 'edit-confirm' : state.mode.state,
          editWindow: state.mode.state === 'create' ? 'detail' : null,
          material: state.mode.state === 'create' ? material : null,
        },
        flowDetail: Object.assign({}, state.flowDetail, {
          deliveryMethod: action.payload as DeliveryMethod,
          materials: fromToChanged ? [] : state.flowDetail.materials,
          planning: fromToChanged ? initialStateEditFlowDetail.flowDetail.planning : state.flowDetail.planning,
        }),
        flowDate: state.flowDate,
      });
    }

    case actions.FLOWDETAIL_BACK_TO_DELIVERY: {
      return Object.assign({}, state, {
        mode: {
          type: action.payload,
          state: state.mode.state,
          editWindow: 'delivery',
        },
      });
    }

    case actions.FLOWDETAIL_BACK_TO_DETAIL: {
      const material = util.deepCopy(initialStatePackingMaterial) as PackingMaterial;
      material.internalId = Math.max(0, ...state.flowDetail.materials.map(m => m.internalId)) + 1;
      material.isNew = true;
      return Object.assign({}, state, {
        mode: {
          type: action.payload,
          state: state.mode.state,
          editWindow: 'detail',
          material: state.mode.state === 'create' ? material : null,
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_EDIT_FLOW_DETAIL: {
      const material = util.deepCopy(initialStatePackingMaterial) as PackingMaterial;
      material.internalId = Math.max(0, ...state.flowDetail.materials.map(m => m.internalId)) + 1;
      material.isNew = true;
      return Object.assign({}, state, {
        mode: {
          material: !state.flowDetail.lateHandshake && material, // Late handshakes cannot add new materials
          type: state.mode.type,
          state: state.mode.state === 'edit-confirm' ? 'edit' : state.mode.state,
          editWindow: 'detail',
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_EDIT_FLOW_PLANNING: {
      return Object.assign({}, state, {
        mode: {
          type: state.mode.type,
          state: state.mode.state,
          editWindow: 'planning',
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_START_EDIT_MATERIAL: {
      return Object.assign({}, state, {
        mode: {
          type: 'order',
          state: state.mode.state,
          editWindow: state.mode.editWindow,
          material: action.payload.material,
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_SAVE_MATERIAL: {
      // eslint-disable-next-line prefer-const
      let { material, isDispute, definitions } = action.payload;
      const existingMaterial = state.flowDetail.materials.find(m => m.internalId === material.internalId);

      material = {
        ...material,
        isEditable: true,
      };

      if (isDispute) {
        material = {
          ...material,
          disputeQuantity: material.packingQuantity,
          definitiveQuantity: (existingMaterial ? existingMaterial.definitiveQuantity : null) || material.definitiveQuantity || '0',
        };
      }

      // bugfix EPS-3344: editing a packing that is not in contract anymore resulted in an empty packingId
      if (existingMaterial) {
        material = {
          ...existingMaterial,
          ...material,
        };

        if (util.isNilOrEmpty(material.packingId)) {
          material.packingId = existingMaterial.packingId;
        }
      }

      const returnState = Object.assign({}, state, {
        mode: {
          type: 'flow',
          state: state.mode.state,
          editWindow: state.mode.editWindow,
          material: !state.flowDetail.lateHandshake && createNewMaterial(material.internalId), // Late handshakes cannot add new materials
          saving: false,
        },
        flowDetail: {
          ...state.flowDetail,
          planning: !existingMaterial && state.mode.state === 'create' ? initialStateEditFlowDetail.flowDetail.planning : state.flowDetail.planning,
        },
      });
      return disputeReducer(addOrReplaceMaterial(material, returnState, definitions), material);
    }

    case actions.FLOWDETAIL_CREATE_DELETE_MATERIAL: {
      return disputeReducer(
        Object.assign({}, state, {
          flowDetail: Object.assign({}, state.flowDetail, {
            materials: state.flowDetail.materials.filter(m => m !== action.payload),
          }),
        })
      );
    }

    case actions.FLOWDETAIL_EDIT_DELIVERY_METHOD: {
      return Object.assign({}, state, {
        mode: {
          type: state.mode.type,
          state: state.mode.state === 'edit-confirm' ? 'edit' : state.mode.state,
          editWindow: 'delivery',
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_SAVE_FLOW_DETAIL: {
      return Object.assign({}, state, {
        mode: {
          type: 'flow',
          state: state.mode.state === 'edit' ? 'edit-confirm' : state.mode.state,
          editWindow: state.mode.state === 'create' ? 'planning' : null,
          material: null,
        },
      });
    }

    case actions.FLOWDETAIL_CREATE_SAVE_PLANNING: {
      const form = action.payload;
      return Object.assign({}, state, {
        mode: {
          type: state.mode.type,
          state: 'create-confirm',
          editWindow: null,
          material: null,
        },
        flowDetail: Object.assign({}, state.flowDetail, {
          planning: Object.assign({}, state.flowDetail.planning, {
            flowDate: form.flowDate,
          }),
        }),
      });
    }

    case actions.FLOWDETAIL_OPEN: {
      return Object.assign({}, state, {
        mode: {
          type: 'flow',
          state: 'view',
          editWindow: null,
          material: null,
          loading: true,
        },
        flowDetail: null,
      });
    }

    case actions.FLOWDETAIL_OPEN_SUCCESS: {
      const editFlowDetail = readFlowTO(
        action.payload.flowDetail,
        action.payload.customerInfo,
        action.payload.contractInfo,
        false,
        'FLOWR',
        action.payload.definitions,
        action.payload.customer
      );
      return {
        ...editFlowDetail,
        customer: action.payload.customer,
      } as EditFlowDetail;
    }

    case actions.FLOWDETAIL_COPY_OPEN_FLOW_SUCCESS: {
      const editFlowDetail = readFlowTO(
        action.payload.flowDetail,
        action.payload.customerInfo,
        action.payload.contractInfo,
        false,
        'FLOWR',
        action.payload.definitions,
        action.payload.customer,
        true
      );
      return {
        ...editFlowDetail,
        flowDetail: {
          ...editFlowDetail.flowDetail,
          etmOrderNumber: null,
          salesOrderNumber: null,
          planning: {
            ...editFlowDetail.flowDetail.planning,
            flowDate: null,
            loadingDate: null,
            requestedUnloadingDate: null,
            confirmedUnloadingDate: null,
          },
        },
        mode: {
          ...editFlowDetail.mode,
          type: state.mode.type,
          state: 'create-confirm',
          editWindow: 'planning',
          material: null,
          loading: false,
          loadSuccess: true,
          saving: false,
          saved: false,
          saveSuccess: false,
          datesLoading: false,
        },
      };
    }

    case actions.FLOWDETAIL_OPEN_FAIL: {
      return Object.assign({}, state, {
        mode: Object.assign({}, state.mode, {
          loading: false,
          loadSuccess: false,
        }),
      });
    }

    case actions.FLOWDETAIL_CREATE_OR_UPDATE_SUBMIT: {
      return Object.assign({}, state, {
        mode: { ...state.mode, saving: true, saveSuccess: false, saved: false },
      });
    }

    case actions.FLOWDETAIL_CREATE_OR_UPDATE_SUBMIT_SUCCESS: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          saving: false,
          saveSuccess: true,
          saved: true,
        },
        flowDetail: Object.assign({}, state.flowDetail, {
          etmOrderNumber: action.payload.result.etmOrderNumber,
        }),
      });
    }

    case actions.FLOWDETAIL_CREATE_OR_UPDATE_SUBMIT_FAIL: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          saving: false,
          saveSuccess: false,
          saved: false,
        },
      });
    }

    case actions.FLOWDETAIL_ACCEPT_FLOW_START: {
      return Object.assign({}, state, {
        mode: { ...state.mode, saving: true, saveSuccess: false, saved: false },
      });
    }

    case actions.FLOWDETAIL_ACCEPT_FLOW_SUCCESS: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          saving: false,
          saveSuccess: true,
          saved: true,
        },
      });
    }

    case actions.FLOWDETAIL_ACCEPT_FLOW_FAIL: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          saving: false,
          saveSuccess: false,
          saved: true,
        },
      });
    }

    // start editing a flow with state 'waiting for dispute'
    case actions.FLOWDETAIL_EDIT_DISPUTE: {
      const editFlowDetail = readFlowTO(
        action.payload.flowDetail,
        action.payload.customerInfo,
        action.payload.contractInfo,
        false,
        'FLOWR',
        action.payload.definitions,
        action.payload.customer
      );

      // check which materials can be edited
      const materials = editFlowDetail.flowDetail.materials.map(material => {
        return {
          ...material,
          isEditable: material.status === editFlowDetail.flowDetail.status,
        };
      });

      // make array of packingIds that are editable / instertable: only those with equal status and with quantity > 0
      // eg http://localhost:4200/#/flows/0102987673/dispute on QAS
      const limitEditToPackingIds = editFlowDetail.flowDetail.materials
        .filter(m => {
          return m.isEditable && m.definitiveQuantity > 0;
          // return m.definitiveQuantity > 0;
        })
        .map(m => m.packingId)
        .filter((id, index, arr) => arr.indexOf(id) === index);

      // distribute definitiveQuantities, if sum(definitiveQuantities) <= sum(originalQuantities) for a packingId, then quantities can be distributed
      const materialsPerPacking = materials
        .filter(m => m.isEditable)
        .reduce((result, material) => {
          if (result[material.packingId]) {
            result[material.packingId].push(material);
          } else {
            result[material.packingId] = [material];
          }
          return result;
        }, {});

      const balanceQuantities = (materials: PackingMaterial[]) => {
        const needsQuantityBalancing = !!materials.find(m => util.toNumber(m.originalQuantity) < util.toNumber(m.definitiveQuantity));
        let resetDisputeQuantity = true;
        if (needsQuantityBalancing) {
          let remainingDefinitiveQuantity = materials.reduce((sum: number, material: PackingMaterial) => sum + util.toNumber(material.definitiveQuantity), 0);
          materials.forEach((material: PackingMaterial) => {
            const disputeQuantity = Math.min(util.toNumber(material.originalQuantity), remainingDefinitiveQuantity);
            remainingDefinitiveQuantity -= disputeQuantity;
            material.disputeQuantity = disputeQuantity;
            material.packingQuantity = disputeQuantity;
          });
          // is success ?
          if (remainingDefinitiveQuantity === 0) {
            resetDisputeQuantity = false;
          }
        }
        if (resetDisputeQuantity) {
          materials.forEach((material: PackingMaterial) => {
            material.disputeQuantity = material.definitiveQuantity;
            material.packingQuantity = material.definitiveQuantity;
          });
        }
      };

      Object.keys(materialsPerPacking).forEach(key => balanceQuantities(materialsPerPacking[key]));

      // return state
      return disputeReducer({
        ...state,
        ...editFlowDetail,
        isDisputeMgmt: true,
        limitEditToPackingIds,
        flowDetail: {
          ...editFlowDetail.flowDetail,
          materials,
        },
        mode: {
          ...editFlowDetail.mode,
          type: 'flow',
          state: 'edit',
          editWindow: 'detail',
          material: createNewMaterial(),
          saving: false,
          saveSuccess: false,
          saved: false,
        },
      });
    }

    case actions.FLOWDETAIL_EDIT_FLOW: {
      const returnState = readFlowTO(
        action.payload.flowDetail,
        action.payload.customerInfo,
        action.payload.contractInfo,
        false,
        'FLOWR',
        action.payload.definitions,
        action.payload.customer,
        true
      );
      return Object.assign({}, state, {
        ...returnState,
        mode: {
          ...returnState.mode,
          type: 'flow',
          state: 'edit',
          editWindow: 'delivery',
          saving: false,
          saveSuccess: false,
          saved: false,
        },
      });
    }

    case actions.FLOWDETAIL_EDIT_ACCEPT_FLOW: {
      const returnState = readFlowTO(
        action.payload.flowDetail,
        action.payload.customerInfo,
        action.payload.contractInfo,
        true,
        'FLOWH',
        action.payload.definitions,
        action.payload.customer,
        true
      );
      return Object.assign({}, state, {
        ...returnState,
        mode: {
          ...returnState.mode,
          type: 'flow',
          state: returnState.flowDetail.lateHandshake ? 'edit-confirm' : 'edit',
          editWindow: returnState.flowDetail.lateHandshake ? null : 'delivery',
          saving: false,
          saveSuccess: false,
          saved: false,
        },
      });
    }

    case actions.FLOWDETAIL_REQUEST_DELIVERY_DATES_SUCCESS: {
      return Object.assign({}, state, {
        datesRange: action.payload,
      });
    }

    case actions.FLOWDETAIL_REQUEST_DELIVERY_DATES_FAIL: {
      return Object.assign({}, state, {
        mode: {
          ...state.mode,
          openingWindow: null,
        },
      });
    }

    case actions.FLOWDETAIL_CHECK_FLOW_DATE: {
      return {
        ...state,
        flowDetail: {
          ...state.flowDetail,
          flowDateError: null,
          flowDateErrorObject: {}
        },
      };
    }

    case actions.FLOWDETAIL_CHECK_FLOW_DATE_SUCCESS: {
      return Object.assign({}, state, {
        flowDate: action.payload,

        flowDetail: {
          ...state.flowDetail,
          deliveryMethod: {
            ...state.flowDetail.deliveryMethod,
            clearing: action.payload.CheckFlowDateResponse.clearing && action.payload.CheckFlowDateResponse.clearing === 'X' ? true : false,
          },
          flowDateError: null,
          flowDateErrorObject: {}
        },
      });
    }

    case actions.FLOWDETAIL_CHECK_FLOW_DATE_ERROR: {
      return Object.assign({}, state, {
        flowDetail: {
          ...state.flowDetail,
          flowDateError: action.payload.Message,
          flowDateErrorObject: { [action.payload.Message]: true }
        },
      });
    }

    case actions.FLOWDETAIL_RESET_MATERIALS_AND_PLANNING: {
      return {
        ...state,
        mode: {
          ...state.mode,
          material: {
            internalId: 1,
          },
        },
        flowDetail: {
          ...state.flowDetail,
          materials: [],
          planning: initialStateEditFlowDetail.flowDetail.planning,
        },
      };
    }

    case actions.FLOWDETAIL_SORT_MATERIALS: {
      const materials = sortMaterials(state.flowDetail.materials, action.payload, action.payload.definitions);
      return {
        ...state,
        sort: action.payload,
        flowDetail: {
          ...state.flowDetail,
          materials,
        },
      };
    }

    default: {
      return state;
    }
  }
}

